<div class="dm-app">

  <!-- Barra superiore -->
  <header class="dm-topbar">
    <img src="assets/images/logos/logo_dm.svg" alt="DM Manufacturing" class="dm-logo"/>
    <div class="dm-topbar-actions">
      <button class="dm-chip-btn" [matMenuTriggerFor]="langMenu" [attr.aria-label]="'documents.language' | translate">
        <i-tabler name="world" class="icon-18"></i-tabler>
        <span>{{ currentLanguage | uppercase }}</span>
      </button>
      <mat-menu #langMenu="matMenu">
        @for (lang of languages; track lang.code) {
          <button mat-menu-item (click)="setLanguage(lang.code)">{{ lang.language }}</button>
        }
      </mat-menu>

      <button class="dm-avatar" [matMenuTriggerFor]="userMenu" [attr.aria-label]="'documents.account' | translate">
        {{ userInitials }}
      </button>
      <mat-menu #userMenu="matMenu">
        <div class="dm-user-info">
          <strong>{{ user?.name }} {{ user?.lastname }}</strong>
          <span>{{ user?.email }}</span>
        </div>
        <button mat-menu-item (click)="logout()">
          <i-tabler name="logout" class="icon-18 m-r-8"></i-tabler>
          {{ 'documents.logout' | translate }}
        </button>
      </mat-menu>
    </div>
  </header>

  <main class="dm-main">

    <!-- Libreria documenti -->
    <section class="dm-panel dm-library">
      <div class="dm-panel-head">
        <h2>{{ 'documents.my_documents' | translate }}</h2>
        <span class="dm-count">{{ readyDocuments.length }}/{{ documents.length }} {{ 'documents.ready_short' | translate }}</span>
      </div>

      <input #fileInput type="file" accept="application/pdf,.pdf" multiple hidden (change)="onFileSelected($event)"/>
      <button type="button" class="dm-dropzone" [class.active]="dragOver"
              (click)="fileInput.click()" (dragover)="onDragOver($event)"
              (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
        @if (uploadingCount > 0) {
          <mat-spinner diameter="28"></mat-spinner>
          <strong>{{ 'documents.uploading' | translate }} ({{ uploadingCount }})</strong>
        } @else {
          <i-tabler name="cloud-upload" class="icon-32"></i-tabler>
          <strong>{{ (dragOver ? 'documents.drop_active' : 'documents.drop_title') | translate }}</strong>
          <span>{{ 'documents.drop_hint' | translate }}</span>
        }
      </button>

      <div class="dm-doc-list">
        @if (documents.length > 0) {
          <button type="button" class="dm-doc dm-doc-all" [class.active]="scope === ALL" (click)="selectScope(ALL)">
            <span class="dm-doc-icon all"><i-tabler name="stack-2" class="icon-20"></i-tabler></span>
            <span class="dm-doc-body">
              <span class="dm-doc-name">{{ 'documents.all_documents' | translate }}</span>
              <span class="dm-doc-meta">{{ 'documents.all_documents_hint' | translate }}</span>
            </span>
          </button>
        }

        @if (listLoaded && documents.length === 0 && uploadingCount === 0) {
          <p class="dm-empty-list">{{ 'documents.empty' | translate }}</p>
        }

        @for (doc of documents; track doc.uuid) {
          <div class="dm-doc" role="button" tabindex="0" [class.active]="scope === doc.uuid"
               [class.indexing]="doc.rag_status === RagStatus.PENDING"
               (click)="selectScope(doc.uuid!)" (keydown.enter)="selectScope(doc.uuid!)">
            <span class="dm-doc-icon"><i-tabler name="file-type-pdf" class="icon-20"></i-tabler></span>
            <span class="dm-doc-body">
              <span class="dm-doc-name" [title]="doc.name">{{ doc.name }}</span>
              <span class="dm-doc-meta">
                {{ doc['created_at'] | date:'dd/MM/yyyy' }}@if (formatSize(doc['dimension_file'])) { · {{ formatSize(doc['dimension_file']) }} }
              </span>
              @switch (doc.rag_status) {
                @case (RagStatus.ANALYZED) {
                  <span class="dm-status ready"><i></i>{{ 'documents.status_ready' | translate }}</span>
                }
                @case (RagStatus.PENDING) {
                  <span class="dm-status pending"><i></i>{{ 'documents.status_pending' | translate }}</span>
                }
                @case (RagStatus.ERROR) {
                  <span class="dm-status error" [matTooltip]="doc['rag_error'] || ''"><i></i>{{ 'documents.status_error' | translate }}</span>
                }
                @default {
                  <span class="dm-status idle"><i></i>{{ 'documents.status_not_indexed' | translate }}</span>
                }
              }
            </span>
            <button type="button" class="dm-icon-btn" (click)="openPdf(doc, undefined, $event)" [matTooltip]="'documents.open_pdf' | translate">
              <i-tabler name="external-link" class="icon-18"></i-tabler>
            </button>
            @if (doc.rag_status === RagStatus.ERROR || doc.rag_status === RagStatus.NOT_ANALYZED) {
              <button type="button" class="dm-icon-btn" (click)="startIndexing(doc, $event)" [matTooltip]="'documents.index' | translate">
                <i-tabler name="refresh" class="icon-18"></i-tabler>
              </button>
            }
            <button type="button" class="dm-icon-btn danger" (click)="deleteDocument(doc, $event)" [matTooltip]="'documents.delete' | translate">
              <i-tabler name="trash" class="icon-18"></i-tabler>
            </button>
          </div>
        }
      </div>
    </section>

    <!-- Assistente AI -->
    <section class="dm-panel dm-chat">
      <div class="dm-chat-head">
        <span class="dm-ai-badge"><i-tabler name="sparkles" class="icon-20"></i-tabler></span>
        <div class="dm-chat-title">
          <h2>{{ 'documents.ai_title' | translate }}</h2>
          <span>
            {{ 'documents.scope_label' | translate }}
            <strong>{{ selectedDocument ? selectedDocument.name : ('documents.all_documents' | translate) }}</strong>
          </span>
        </div>
        <button type="button" class="dm-ghost-btn" (click)="newConversation()" [disabled]="sending || messages.length === 0">
          <i-tabler name="message-plus" class="icon-18"></i-tabler>
          <span>{{ 'documents.new_conversation' | translate }}</span>
        </button>
      </div>

      <div class="dm-messages" #messagesContainer>
        @if (messages.length === 0) {
          <div class="dm-welcome">
            <span class="dm-welcome-orb"><i-tabler name="sparkles" class="icon-32"></i-tabler></span>
            @if (documents.length === 0) {
              <h3>{{ 'documents.welcome_title' | translate }}</h3>
              <p>{{ 'documents.welcome_empty' | translate }}</p>
              <ol class="dm-steps">
                <li>
                  <span class="dm-step-icon"><i-tabler name="cloud-upload" class="icon-20"></i-tabler></span>
                  <strong>{{ 'documents.step1_title' | translate }}</strong>
                  <span>{{ 'documents.step1_text' | translate }}</span>
                </li>
                <li>
                  <span class="dm-step-icon"><i-tabler name="brain" class="icon-20"></i-tabler></span>
                  <strong>{{ 'documents.step2_title' | translate }}</strong>
                  <span>{{ 'documents.step2_text' | translate }}</span>
                </li>
                <li>
                  <span class="dm-step-icon"><i-tabler name="message-question" class="icon-20"></i-tabler></span>
                  <strong>{{ 'documents.step3_title' | translate }}</strong>
                  <span>{{ 'documents.step3_text' | translate }}</span>
                </li>
              </ol>
              <button type="button" class="dm-primary-btn" (click)="fileInput.click()" [disabled]="uploadingCount > 0">
                <i-tabler name="upload" class="icon-18"></i-tabler>
                {{ 'documents.upload_cta' | translate }}
              </button>
            } @else if (!canChat && selectedDocument?.rag_status === RagStatus.PENDING) {
              <h3>{{ 'documents.indexing_title' | translate }}</h3>
              <p>{{ 'documents.indexing_hint' | translate }}</p>
            } @else if (!canChat && selectedDocument?.rag_status === RagStatus.ERROR) {
              <h3>{{ 'documents.status_error' | translate }}</h3>
              <p>{{ 'documents.error_hint' | translate }}</p>
              @if (selectedDocument?.['rag_error']) {
                <p class="dm-error-detail">{{ selectedDocument?.['rag_error'] }}</p>
              }
            } @else if (!canChat) {
              <h3>{{ 'documents.indexing_title' | translate }}</h3>
              <p>{{ 'documents.wait_hint' | translate }}</p>
            } @else {
              <h3>{{ 'documents.ask_title' | translate }}</h3>
              <p>{{ 'documents.ask_hint' | translate }}</p>
              <div class="dm-suggestions">
                @for (s of suggestions; track s) {
                  <button type="button" (click)="useSuggestion(s)">{{ s | translate }}</button>
                }
              </div>
            }
          </div>
        }

        @for (msg of messages; track $index) {
          <div class="dm-msg" [class.user]="msg.role === 'user'" [class.bot]="msg.role === 'bot'" [class.error]="msg.error">
            @if (msg.role === 'bot') {
              <span class="dm-msg-avatar"><i-tabler name="sparkles" class="icon-16"></i-tabler></span>
            }
            <div class="dm-msg-content">
              @if (msg.html) {
                <div class="dm-msg-bubble dm-rich" [innerHTML]="msg.html"></div>
              } @else {
                <div class="dm-msg-bubble">{{ msg.text }}</div>
              }
              @if (msg.sources?.length) {
                <div class="dm-sources">
                  @for (src of msg.sources; track src.name) {
                    <span class="dm-source">
                      <i-tabler name="file-text" class="icon-14"></i-tabler>
                      <span class="dm-source-name" [title]="src.name">{{ src.name }}</span>
                      @for (p of src.pages; track p) {
                        <button type="button" (click)="openSource(src, p)" [matTooltip]="'documents.open_page' | translate">
                          {{ 'documents.pages' | translate }} {{ p }}
                        </button>
                      }
                    </span>
                  }
                </div>
              }
              <div class="dm-msg-footer">
                <span class="dm-msg-time">{{ msg.timestamp | date:'HH:mm' }}</span>
                @if (msg.role === 'bot' && !msg.error) {
                  <button type="button" class="dm-msg-action" (click)="copyAnswer(msg)">
                    <i-tabler [name]="msg.copied ? 'check' : 'copy'" class="icon-14"></i-tabler>
                    {{ (msg.copied ? 'documents.copied' : 'documents.copy') | translate }}
                  </button>
                }
              </div>
            </div>
          </div>
        }

        @if (sending) {
          <div class="dm-msg bot">
            <span class="dm-msg-avatar"><i-tabler name="sparkles" class="icon-16"></i-tabler></span>
            <div class="dm-msg-content">
              <div class="dm-msg-bubble dm-typing">
                <span></span><span></span><span></span>
                <em>{{ 'documents.thinking' | translate }}</em>
              </div>
            </div>
          </div>
        }
      </div>

      <div class="dm-composer" [class.disabled]="!canChat">
        <textarea
          #chatInput
          [(ngModel)]="inputText"
          (input)="autoGrowInput()"
          (keydown.enter)="onEnterKey($event)"
          [placeholder]="(canChat ? 'documents.placeholder' : 'documents.placeholder_disabled') | translate"
          [disabled]="sending || !canChat"
          rows="1"
          autocomplete="off"></textarea>
        <button type="button" class="dm-send" (click)="sendMessage()"
                [disabled]="sending || !canChat || !inputText.trim()" [attr.aria-label]="'documents.send' | translate">
          <i-tabler name="send" class="icon-20"></i-tabler>
        </button>
      </div>
    </section>
  </main>
</div>
